import { Box, CircularProgress, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, ModalContentContainer, TextField } from '@/components';
import { Routes } from '@/constants';
import { useForm } from '@/hooks';
import {
  useResetPasswordWithTokenMutation,
  useValidatePasswordResetTokenQuery,
} from '@/store';
import { SnackbarUtils } from '@/utils';

import { PageContainer } from '../components';

type ResetPasswordFormState = {
  newPassword: string;
  confirmPassword: string;
};

export const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { form, errors, setErrors, validateForm, onChange } =
    useForm<ResetPasswordFormState>({
      newPassword: '',
      confirmPassword: '',
    });

  const token = new URLSearchParams(location.search).get('token') || '';

  const { data: isTokenValid, isLoading: isTokenValidationLoading } =
    useValidatePasswordResetTokenQuery({ token });

  const [resetPassword] = useResetPasswordWithTokenMutation();

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    try {
      const res = await resetPassword({
        password: form.newPassword.trim(),
        token,
      }).unwrap();
      if (res) {
        SnackbarUtils.success('Password updated successfully.');
        navigate(Routes.LOGIN);
      } else {
        setErrors(errors => ({
          ...errors,
          newPassword:
            'Something went wrong. Please try again or contact support.',
        }));
      }
    } catch (error) {
      const { status, data } = error as { status: number; data: any };
      if (status === 400 && data?.message) {
        setErrors(prev => ({
          ...prev,
          newPassword: data.message,
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          newPassword:
            'Something went wrong. Please try again or contact support.',
        }));
      }
    }
  };
  return (
    <PageContainer hideCertificationInfo>
      <ModalContentContainer>
        {isTokenValidationLoading && (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CircularProgress />
          </Box>
        )}
        {!isTokenValidationLoading && isTokenValid && (
          <>
            <Typography variant="h1">Reset Password</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Enter your new password below.
            </Typography>
            <TextField
              id="newPassword"
              value={form.newPassword}
              fullWidth
              label="New Password"
              placeholder="Enter your new password"
              type="password"
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              sx={{ mt: 2 }}
              onChange={onChange}
            />
            <TextField
              id="confirmPassword"
              value={form.confirmPassword}
              fullWidth
              label="Confirm Password"
              placeholder="Re-enter your new password"
              type="password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              sx={{ mt: 2 }}
              onChange={onChange}
            />
            <Button
              variant="primary"
              label="Reset Password"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={onSubmit}
            />
          </>
        )}
        {!isTokenValidationLoading && !isTokenValid && (
          <>
            <Typography variant="h1">Invalid Token</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              The password reset token is invalid or has expired. Please try
              again.
            </Typography>
            <Button
              variant="primary"
              label="Resend Email"
              fullWidth
              sx={{ mt: 2, height: 48, fontSize: 16 }}
              onClick={() => navigate(Routes.FORGOT_PASSWORD)}
            />
          </>
        )}
      </ModalContentContainer>
    </PageContainer>
  );
};
